import * as ko from "knockout";
import DOMPurify from 'dompurify';
import { AuthAttribute, AuthMethod, AuthRequest, NullAuthMethod } from '../contracts';
import template from './FormsAttribute.html';


class FormsAttribute {

  type: ko.Observable<string> = ko.observable<string>("text");
  label: ko.Observable<string> = ko.observable<string>("");
  name: ko.Observable<string> = ko.observable<string>("");
  placeHolder: ko.Observable<string> = ko.observable<string>("");
  value: ko.Observable<string | null> = ko.observable<string>(null);
  sortOrder: ko.Observable<number> = ko.observable<number>(0);
  imageSrc: ko.Observable<string | null> = ko.observable<string>(null);
  imageAlt: ko.Observable<string | null> = ko.observable<string>(null);
  rememberMe: ko.Observable<string> = ko.observable<string>("");
  persisted: ko.Observable<boolean> = ko.observable<boolean>(false);
  validationError: ko.Observable<string | null> = ko.observable<string>(null);
  checked: ko.Observable<boolean> = ko.observable<boolean>(false);
  linkUrl: ko.Observable<string | null> = ko.observable<string>(null);
  hasFocus: ko.Observable<boolean> = ko.observable<boolean>(false);
  message: ko.Observable<string> = ko.observable<string>("");
  canBePersisted: ko.Observable<boolean> = ko.observable(false);

  // Computed properties - initialized in constructor
  inputEnabled: ko.PureComputed<boolean>;
  hasValidationError: ko.PureComputed<boolean>;
  isVisible: ko.PureComputed<boolean>;
  isCheckbox: ko.PureComputed<boolean>;
  isLink: ko.PureComputed<boolean>;
  isMessage: ko.PureComputed<boolean>;
  isMarkdownArea: ko.PureComputed<boolean>;
  selectedMethod: ko.PureComputed<AuthMethod>;
  selectedLanguage: ko.PureComputed<string>;

  constructor(params: { attribute: AuthAttribute }) {
    // Initialize computed properties
    this.inputEnabled = ko.pureComputed<boolean>(() => !this.persisted());
    this.hasValidationError = ko.pureComputed<boolean>(() => this.validationError() != null);
    this.isVisible = ko.pureComputed<boolean>(() => {
      const t = this.type();
      const n = this.name();
      // Hide CSRF token and other hidden fields
      if (n === AuthRequest.AttributeCsrfToken || t === "hidden") {
        return false;
      }
      return t === "text" || t === "password";
    });
    this.isCheckbox = ko.pureComputed<boolean>(() => this.type() === "checkbox");
    this.isLink = ko.pureComputed<boolean>(() => this.type() === "link");
    this.isMessage = ko.pureComputed<boolean>(() => this.type() === "message");
    this.isMarkdownArea = ko.pureComputed<boolean>(() => this.type() === "markdown");
    this.selectedMethod = ko.pureComputed(() => global.Logonme.SelectedMethod() ?? new NullAuthMethod());
    this.selectedLanguage = ko.pureComputed(() => global.Logonme.SelectedLanguage());

    var attribute = params.attribute;
    this.name(attribute.name ?? "");

    this.hasFocus(params.attribute.hasFocus);
    this.value(attribute.value);
    this.value.subscribe((value: string | null) => {
      attribute.value = value;
    });

    // Initialize checkbox checked state from value
    this.checked(attribute.value === "true" || attribute.value === "1" || attribute.value === "on");
    this.checked.subscribe((isChecked: boolean) => {
      attribute.value = isChecked ? "true" : "false";
    });

    // Initialize link URL from value
    this.linkUrl(attribute.value);

    this.persisted(attribute.persisted);
    this.persisted.subscribe((value: boolean) => {
      attribute.persisted = value;
    })

    this.updateStrings(this.selectedLanguage(), this.selectedMethod(), attribute);

    this.selectedLanguage.subscribe((lang: string) => {
      this.updateStrings(lang, this.selectedMethod(), attribute)
    })
  }

  private focusNextInput(buttonElementId: string) {
    var focusInput = document.activeElement;
    if (focusInput?.tagName.toLowerCase() === "input") {
      var inputs = [];
      var all = document.getElementsByTagName("input");;
      for (var i = 0; i < all.length; i++) {
        if (all[i].getAttribute("type") === "text" || all[i].getAttribute("type") === "password") {
          inputs.push(all[i]);
        }
      }
      focusInput = document.activeElement;
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i] === focusInput) {
          var next = inputs[i + 1];
          if (next && next.focus) {
            next.focus();
          } else {
            var element = document.getElementById(buttonElementId);
            element?.focus();
          }
          break;
        }
      }
    }
  }

  private handleKeyPressed(data: any, event: KeyboardEvent) {
    switch (event.code) {
      case "Enter":
      case "NumpadEnter":
        this.focusNextInput("auth.submitButton")
        break;
    }
    return true;
  }

  private updateStrings(lang: string, method: AuthMethod, attribute: AuthAttribute) {

    this.rememberMe(global.Logonme.LocalizationManager.getText("auth-form-remember-me", lang));
    var canBePersisted = (attribute.name == AuthRequest.AttributePwdUsername || attribute.name == AuthRequest.AttributeBankIdPnr || attribute.name == AuthRequest.AttributeFido2Username);
    this.canBePersisted(canBePersisted);

    var aid = attribute.name ?? "";
    var errorId = attribute.validationError ?? "";

    // Get the inputType from localization first
    var inputType = global.Logonme.LocalizationManager.getPropertyX(method, ['attributes', aid, 'inputType'], null, null);
//    console.log('[FormsAttribute] getPropertyX for', aid, 'inputType:', inputType, 'method:', method);
    this.type(inputType ?? "text");

    this.placeHolder(global.Logonme.LocalizationManager.getPropertyX(method, ['attributes', aid, 'placeHolder'], null, lang));
    this.label(global.Logonme.LocalizationManager.getPropertyX(method, ['attributes', aid, 'text'], null, lang));
    this.sortOrder(global.Logonme.LocalizationManager.getPropertyX(method, ['attributes', aid, 'sortOrder'], 0, null));
    this.validationError(global.Logonme.LocalizationManager.getMessage(method, errorId, null, lang));

    var isImage = (attribute.name == AuthRequest.AttributeBankIdChallengeQr || attribute.name == AuthRequest.AttributeOtpEnrollmentQrCode || attribute.name == AuthRequest.AttributeCaptchaImage);
    if (isImage) {
      this.imageSrc(attribute.value);
      this.imageAlt(attribute.name);
    }

    // Check inputType from localization, not attribute.type
    var isMessage = (inputType == "message" || inputType == "markdown" || attribute.name == AuthRequest.AttributeChallengeMessage);
//    console.log('[FormsAttribute] isMessage check:', aid, 'inputType:', inputType, 'isMessage:', isMessage, 'value:', attribute.value);
    if (isMessage) {
      if (attribute.value != null) {
        // TODO: allow localized links & messages
        //  this.message = global.Logonme.LocalizationManager.getMessage(method, attribute.value ?? "", attribute.value , lang);
        // For message type, use the raw value as the message content (for service agreements, etc.)
        this.message(DOMPurify.sanitize(attribute.value));
      }
    }

    var isLink = (inputType == "link");
    if (isLink) {
      this.linkUrl(attribute.value);
    }

    // Debug: log state after update
//    this.logState();
  }

  private logState() {
    global.Logonme.log(`[FormsAttribute] ${this.name()} | type=${this.type()} | isMarkdownArea=${this.isMarkdownArea()} | isCheckbox=${this.isCheckbox()} | isLink=${this.isLink()} | message="${this.message()}" | label="${this.label()}"`);
  }
}

export default { viewModel: FormsAttribute, template: template };
